package com.anjuros.website.redesign.sections.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.components.language.ResKey
import com.anjuros.website.redesign.components.RdCopyButton
import com.anjuros.website.redesign.components.RdEyebrow
import com.anjuros.website.redesign.components.RdLede
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.HomeContactCardStyle
import com.anjuros.website.redesign.styles.HomeContactCopyStyle
import com.anjuros.website.redesign.styles.HomeContactDirectStyle
import com.anjuros.website.redesign.styles.HomeContactFormStyle
import com.anjuros.website.redesign.styles.HomeContactSuccessIconStyle
import com.anjuros.website.redesign.styles.HomeContactSuccessIn
import com.anjuros.website.redesign.styles.HomeContactSuccessStyle
import com.anjuros.website.redesign.styles.RdH2Style
import com.anjuros.website.redesign.styles.RdH3Style
import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.templates.selection.components.contactform.ContactForm
import com.anjuros.website.templates.selection.components.contactform.ContactFormInfo
import com.anjuros.website.templates.selection.components.contactform.EMAIL_CONTACT_FORM
import com.anjuros.website.templates.selection.components.contactform.MESSAGE_CONTACT_FORM
import com.anjuros.website.templates.selection.components.contactform.NAME_CONTACT_FORM
import com.anjuros.website.templates.selection.util.Constants.CLIENTS_EMAIL
import com.anjuros.website.templates.selection.util.Constants.CLIENTS_WHATSAPP
import com.anjuros.website.templates.selection.util.Constants.CLIENTS_WHATSAPP_LINK
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** The same fields and backend as the old homepage form, including Turnstile spam protection. */
private val ContactFields = listOf(
    NAME_CONTACT_FORM.copy(index = 0),
    EMAIL_CONTACT_FORM.copy(index = 1),
    MESSAGE_CONTACT_FORM.copy(index = 2),
    ContactFormInfo(
        placeholderText = ResKey.CONTACT_REFERRAL_CODE,
        labelText = ResKey.CONTACT_REFERRAL_CODE_LABEL,
        nameText = "Referral Code",
        required = false,
        validations = listOf(),
        index = 3
    ),
)

@Composable
fun HomeContactSection() {
    RdSection(id = "contact", labelledBy = "contact-h") {
        Div(HomeContactCardStyle.toModifier().toAttrs()) {
            ContactDetails()
            ContactFormBox()
        }
    }
}

@Composable
private fun ContactDetails() {
    Div(HomeContactCopyStyle.toModifier().toAttrs()) {
        RdEyebrow(RdText.HOME_CONTACT_EYEBROW)
        H2(RdH2Style.toModifier().id("contact-h").toAttrs()) { Text(LocalText(RdText.HOME_CONTACT_TITLE)) }
        RdLede(RdText.HOME_CONTACT_LEDE)
        Div(HomeContactDirectStyle.toModifier().toAttrs()) {
            Div {
                A("mailto:$CLIENTS_EMAIL") {
                    Span { Text(LocalText(RdText.HOME_CONTACT_EMAIL)) }
                    B { Text(CLIENTS_EMAIL) }
                }
                RdCopyButton(CLIENTS_EMAIL, RdText.COPY_EMAIL_LABEL)
            }
            Div {
                A(CLIENTS_WHATSAPP_LINK, { attr("target", "_blank"); attr("rel", "noopener") }) {
                    Span { Text(LocalText(RdText.HOME_CONTACT_PHONE)) }
                    B { Text(CLIENTS_WHATSAPP) }
                }
                RdCopyButton(CLIENTS_WHATSAPP, RdText.COPY_WHATSAPP_LABEL)
            }
        }
    }
}

@Composable
private fun ContactFormBox() {
    var success by remember { mutableStateOf(false) }

    Div(HomeContactFormStyle.toModifier().toAttrs()) {
        if (success) {
            ContactSuccess(onSendAnother = { success = false })
        } else {
            ContactForm(ContactFields, buttonColor = RdColor.Brand) { success = true }
        }
    }
}

/** Takes the form's place once the message is sent. Focus moves to the heading so screen readers announce it. */
@Composable
private fun ContactSuccess(onSendAnother: () -> Unit) {
    val reducedMotion = remember { window.matchMedia("(prefers-reduced-motion: reduce)").matches }
    val modifier = HomeContactSuccessStyle.toModifier()
        .thenIf(!reducedMotion) {
            Modifier.animation(HomeContactSuccessIn.toAnimation(350.ms, AnimationTimingFunction.EaseOut, fillMode = AnimationFillMode.Both))
        }
        .attrsModifier { attr("role", "status") }

    Div(modifier.toAttrs()) {
        Span(HomeContactSuccessIconStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
            RdIcons.Check(size = 28)
        }
        H3(RdH3Style.toModifier().attrsModifier { attr("tabindex", "-1") }.toAttrs {
            ref { heading ->
                heading.focus()
                onDispose { }
            }
        }) { Text(LocalText(RdText.HOME_CONTACT_SUCCESS_TITLE)) }
        P { Text(LocalText(RdText.HOME_CONTACT_SUCCESS_TEXT)) }
        Button(attrs = {
            attr("type", "button")
            onClick { onSendAnother() }
        }) { Text(LocalText(RdText.HOME_CONTACT_SUCCESS_AGAIN)) }
    }
}
